import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { gsap } from 'gsap'
import { isNil } from 'lodash'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'

import ImageOne from 'images/services/branding/package/1.webp'
import ImageOneMobile from 'images/services/branding/package/1-mobile.webp'
import ImageTwo from 'images/services/branding/package/2.webp'
import ImageTwoMobile from 'images/services/branding/package/2-mobile.webp'
import ImageThree from 'images/services/branding/package/3.webp'
import ImageThreeMobile from 'images/services/branding/package/3-mobile.webp'
import ImageFour from 'images/services/branding/package/4.webp'
import ImageFourMobile from 'images/services/branding/package/4-mobile.webp'
import ImageFive from 'images/services/branding/package/5.webp'
import ImageFiveMobile from 'images/services/branding/package/5-mobile.webp'
import ImageSix from 'images/services/branding/package/6.webp'
import ImageSixMobile from 'images/services/branding/package/6-mobile.webp'

gsap.registerPlugin(ScrollTrigger)

function NavButton({ index, onClick, isActive, label, navItemElementsRef }) {
  const buttonRef = useRef(null)
  // eslint-disable-next-line no-param-reassign
  navItemElementsRef.current[index] = buttonRef.current

  function onButtonClick() {
    onClick(index)
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      className={cx('branding-package__nav-item', {
        '--active': isActive,
      })}
      onClick={onButtonClick}
    >
      {label}
    </button>
  )
}

NavButton.propTypes = {
  index: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  navItemElementsRef: PropTypes.object.isRequired,
}

function StickyBrandPackage() {
  const containerRef = useRef(null)
  const pinRef = useRef(null)
  const timelineRef = useRef(null)
  const navContainerRef = useRef(null)
  const navItemElementsRef = useRef([])

  const [currentIndex, setCurrentIndex] = useState(0)

  const breakpoints = useBreakpoint()
  const isMobile = breakpoints.m

  useLayoutEffect(() => {
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill(true))
  }, [])

  useEffect(() => {
    init()
    ScrollTrigger.refresh()

    return () => {
      timelineRef.current.kill(true)
    }
  }, [])

  useEffect(() => {
    if (
      isMobile &&
      !isNil(timelineRef.current) &&
      !isNil(navItemElementsRef.current[currentIndex])
    ) {
      navContainerRef.current.scrollTo({
        left:
          navItemElementsRef.current[currentIndex].offsetLeft -
          window.innerWidth / 2 +
          navItemElementsRef.current[currentIndex].offsetWidth / 2,
        behavior: 'smooth',
      })
    }
  }, [currentIndex, isMobile, timelineRef.current])

  function onNavClick(index) {
    setCurrentIndex(index)
    const containerRefElement = containerRef.current

    const ST = timelineRef.current.scrollTrigger
    const tween = timelineRef.current.getById(`image-${index}`)
    const endTime = tween.endTime()
    const totalTime = timelineRef.current.duration()
    const STDist = ST.end - ST.start
    const tweenEndPos = (endTime / totalTime) * STDist + ST.start

    if (index > 0 && index !== 5) {
      window.scrollTo(0, tweenEndPos)
    } else if (index === 5) {
      window.scrollTo(0, tweenEndPos - 1)
    } else {
      window.scrollTo(0, containerRefElement.offsetTop)
    }
  }

  function init() {
    setTimeout(() => {
      const position = {
        start: `top top`,
        bottom: 'bottom bottom',
      }
      ScrollTrigger.create({
        id: 'branding-package',
        trigger: containerRef.current,
        ...position,
        // markers: true,
        scrub: true,
        pin: pinRef.current,
      })
      timelineRef.current = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top bottom',
          end: 'bottom bottom',
          scrub: true,
          // markers: true,
        },
      })
      gsap.utils
        .toArray('.branding-package__content-image')
        .forEach((image, index) => {
          if (index > 0) {
            gsap.set(image, { yPercent: breakpoints.m ? 250 : 200 })
          }
          timelineRef.current.to(
            image,
            {
              id: `image-${index}`,
              yPercent: 0,
              ease: 'none',
              onStart: () => {
                setCurrentIndex(index)
              },
              onReverseComplete: () => {
                setCurrentIndex(index - 1)
              },
            },
            index * 0.75,
          )
        })
    }, 1000)
  }

  return (
    <div ref={containerRef} className="branding-package">
      <div ref={pinRef} className="branding-package__wrapper">
        <div className="branding-package__container">
          <nav ref={navContainerRef} className="branding-package__nav">
            <NavButton
              index={0}
              onClick={onNavClick}
              isActive={currentIndex === 0}
              label="Brand Assets"
              navItemElementsRef={navItemElementsRef}
            />
            <NavButton
              index={1}
              onClick={onNavClick}
              isActive={currentIndex === 1}
              navItemElementsRef={navItemElementsRef}
              label="Color palette"
            />
            <NavButton
              index={2}
              onClick={onNavClick}
              isActive={currentIndex === 2}
              navItemElementsRef={navItemElementsRef}
              label="Style Guide"
            />
            <NavButton
              index={3}
              onClick={onNavClick}
              isActive={currentIndex === 3}
              navItemElementsRef={navItemElementsRef}
              label="Font Kit"
            />
            <NavButton
              index={4}
              onClick={onNavClick}
              isActive={currentIndex === 4}
              navItemElementsRef={navItemElementsRef}
              label="Global Icon Set"
            />
            <NavButton
              index={5}
              onClick={onNavClick}
              isActive={currentIndex === 5}
              navItemElementsRef={navItemElementsRef}
              label="Components"
            />
          </nav>
          <div className="branding-package__content">
            <div className="branding-package__content-image">
              <picture>
                <source media="(max-width: 767px)" srcSet={ImageOneMobile} />
                <source media="(min-width: 768px)" srcSet={ImageOne} />
                {ImageOne && (
                  <img src={ImageOne} alt="Brand Assets" loading="lazy" />
                )}
              </picture>
            </div>
            <div className="branding-package__content-image">
              <picture>
                <source media="(max-width: 767px)" srcSet={ImageTwoMobile} />
                <source media="(min-width: 768px)" srcSet={ImageTwo} />
                {ImageTwo && (
                  <img src={ImageTwo} alt="Color palette" loading="lazy" />
                )}
              </picture>
            </div>
            <div className="branding-package__content-image">
              <picture>
                <source media="(max-width: 767px)" srcSet={ImageThreeMobile} />
                <source media="(min-width: 768px)" srcSet={ImageThree} />
                {ImageThree && (
                  <img src={ImageThree} alt="Style Guide" loading="lazy" />
                )}
              </picture>
            </div>
            <div className="branding-package__content-image">
              <picture>
                <source media="(max-width: 767px)" srcSet={ImageFourMobile} />
                <source media="(min-width: 768px)" srcSet={ImageFour} />
                {ImageFour && (
                  <img src={ImageFour} alt="Font Kit" loading="lazy" />
                )}
              </picture>
            </div>
            <div className="branding-package__content-image">
              <picture>
                <source media="(max-width: 767px)" srcSet={ImageFiveMobile} />
                <source media="(min-width: 768px)" srcSet={ImageFive} />
                {ImageFive && (
                  <img src={ImageFive} alt="Global Icon Set" loading="lazy" />
                )}
              </picture>
            </div>
            <div className="branding-package__content-image">
              <picture>
                <source media="(max-width: 767px)" srcSet={ImageSixMobile} />
                <source media="(min-width: 768px)" srcSet={ImageSix} />
                {ImageSix && (
                  <img src={ImageSix} alt="Components" loading="lazy" />
                )}
              </picture>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StickyBrandPackage
